/**
 * useCountdown.ts
 * Hook for managing countdown timer with auto-submit on timeout.
 */

import { useState, useEffect, useCallback } from 'react';

interface UseCountdownOptions {
  /** Expiry time in milliseconds (Date.now() + duration) */
  expiryTime: number;
  /** Callback when timer reaches zero */
  onTimeout: () => void;
  /** Update interval in milliseconds (default: 500ms) */
  interval?: number;
  /** Pause the countdown */
  paused?: boolean;
}

interface CountdownResult {
  /** Remaining time in milliseconds */
  remaining: number;
  /** Remaining time formatted as MM:SS */
  formatted: string;
  /** Whether timer has expired */
  isExpired: boolean;
  /** Minutes remaining (for display) */
  minutes: number;
  /** Seconds remaining (for display) */
  seconds: number;
}

/**
 * Countdown timer hook with auto-submit capability.
 * Updates every 500ms and calls onTimeout when time expires.
 */
export function useCountdown({
  expiryTime,
  onTimeout,
  interval = 500,
  paused = false,
}: UseCountdownOptions): CountdownResult {
  const [now, setNow] = useState(Date.now());
  const [pausedAt, setPausedAt] = useState<number | null>(null);
  const [adjustedExpiryTime, setAdjustedExpiryTime] = useState(expiryTime);

  // Sync adjustedExpiryTime when expiryTime prop changes (e.g., after restore from storage)
  useEffect(() => {
    setAdjustedExpiryTime(expiryTime);
  }, [expiryTime]);

  useEffect(() => {
    if (paused && pausedAt === null) {
      // Starting pause
      setPausedAt(Date.now());
      return;
    }

    if (!paused && pausedAt !== null) {
      // Resuming from pause
      const pauseDuration = Date.now() - pausedAt;
      setAdjustedExpiryTime(prev => prev + pauseDuration);
      setPausedAt(null);
    }

    if (paused) return; // Don't start timer if paused

    const id = setInterval(() => {
      setNow(Date.now());
    }, interval);

    return () => clearInterval(id);
  }, [interval, paused, pausedAt]);

  const remaining = paused
    ? Math.max(0, adjustedExpiryTime - (pausedAt || Date.now()))
    : Math.max(0, adjustedExpiryTime - now);

  const isExpired = remaining === 0 && !paused;

  // Call timeout callback when expired
  const stableOnTimeout = useCallback(onTimeout, [onTimeout]);

  useEffect(() => {
    if (isExpired && remaining === 0) {
      stableOnTimeout();
    }
  }, [isExpired, remaining, stableOnTimeout]);

  // Calculate display values
  const totalSeconds = Math.floor(remaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  // Format as MM:SS
  const formatted = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

  return {
    remaining,
    formatted,
    isExpired,
    minutes,
    seconds,
  };
}

/**
 * Utility to create expiry time for a given duration.
 */
export function createExpiryTime(durationMinutes: number): number {
  return Date.now() + durationMinutes * 60 * 1000;
}
